import { ReactNode, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { classNames } from "../utils";

type HoverExpandCellProps = {
  collapsed: boolean;
  collapsedContent: ReactNode;
  expandedContent: ReactNode;
  className?: string;
  /** Max-width class for the hover panel; defaults to max-w-md. */
  expandPanelClassName?: string;
};

/**
 * Table cell that shows compact content when collapsed and reveals full content
 * in a tooltip-like panel on hover.
 */
export function HoverExpandCell({
  collapsed,
  collapsedContent,
  expandedContent,
  className,
  expandPanelClassName = "max-w-md",
}: HoverExpandCellProps) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<number>();
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  if (!collapsed) {
    return <div className={className}>{expandedContent}</div>;
  }

  function showPanel() {
    if (hideTimeoutRef.current) {
      window.clearTimeout(hideTimeoutRef.current);
    }
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition({
      top: rect.bottom + 4,
      left: rect.left + rect.width / 2,
    });
    setVisible(true);
  }

  function scheduleHide() {
    hideTimeoutRef.current = window.setTimeout(() => setVisible(false), 120);
  }

  return (
    <>
      <div
        ref={triggerRef}
        className={classNames("cursor-default", className ?? "")}
        onMouseEnter={showPanel}
        onMouseLeave={scheduleHide}
      >
        {collapsedContent}
      </div>
      {visible &&
        createPortal(
          <div
            className="fixed z-[9999] -translate-x-1/2"
            style={{ top: position.top, left: position.left }}
            onMouseEnter={showPanel}
            onMouseLeave={scheduleHide}
          >
            <div
              className={classNames(
                "min-w-max rounded-lg border border-gray-300 bg-white p-3 text-xs shadow-lg",
                expandPanelClassName
              )}
            >
              {expandedContent}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
